import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Calendar, Clock, Check } from "lucide-react";
import SEO from "@/components/SEO";
import { getPostBySlug, POSTS } from "@/data/blog";
import { ATTORNEY } from "@/data/content";

const SITE_URL = "https://joehelfrickinjuryattorney.com";

function Block({ block }) {
    switch (block.type) {
        case "h2":
            return (
                <h2 className="font-serif text-3xl md:text-4xl text-[#F7F5EB] leading-tight mt-14 mb-5">
                    {block.text}
                </h2>
            );
        case "h3":
            return (
                <h3 className="font-serif text-2xl text-[#F7F5EB] leading-tight mt-10 mb-3">
                    {block.text}
                </h3>
            );
        case "p":
            return (
                <p className="text-[#F7F5EB]/90 text-base md:text-lg leading-relaxed mb-5">
                    {block.text}
                </p>
            );
        case "ul":
            return (
                <ul className="mb-6 space-y-3">
                    {block.items.map((it, i) => (
                        <li
                            key={i}
                            className="flex gap-3 text-[#F7F5EB]/85 text-base leading-relaxed"
                        >
                            <Check
                                size={16}
                                className="text-[#D4AF37] mt-1.5 shrink-0"
                            />
                            <span>{it}</span>
                        </li>
                    ))}
                </ul>
            );
        case "ol":
            return (
                <ol className="mb-6 space-y-5 counter-reset">
                    {block.items.map((it, i) => (
                        <li
                            key={i}
                            className="grid grid-cols-[40px_1fr] gap-4 items-start"
                        >
                            <span className="text-[#D4AF37] font-serif text-2xl leading-none mt-1">
                                {String(i + 1).padStart(2, "0")}
                            </span>
                            <div>
                                <div className="text-[#F7F5EB] font-semibold text-base md:text-lg">
                                    {it.title}
                                </div>
                                <div className="text-[#9FB3D4] text-base mt-1 leading-relaxed">
                                    {it.body}
                                </div>
                            </div>
                        </li>
                    ))}
                </ol>
            );
        case "internalLink":
            return (
                <div className="my-8">
                    <Link
                        to={block.to}
                        className="inline-flex items-center gap-2 text-[#D4AF37] font-semibold border-b border-[#D4AF37]/40 hover:border-[#D4AF37] pb-1 transition-colors"
                    >
                        {block.text} <ArrowRight size={14} />
                    </Link>
                </div>
            );
        case "callout":
            return (
                <aside className="my-12 bg-[#11203D] border-l-4 border-[#D4AF37] p-6 md:p-8">
                    <div className="font-serif text-2xl text-[#F7F5EB]">
                        {block.title}
                    </div>
                    <p className="mt-3 text-[#9FB3D4] leading-relaxed">
                        {block.body}
                    </p>
                    {block.cta && (
                        <Link
                            to={block.cta.to}
                            data-testid="blog-callout-cta"
                            className="mt-6 inline-flex items-center gap-3 bg-[#D4AF37] text-[#07142B] font-bold uppercase tracking-wider text-xs px-6 py-3 hover:bg-[#B8962A] transition-colors"
                        >
                            {block.cta.label} <ArrowRight size={14} />
                        </Link>
                    )}
                    <div className="mt-4">
                        <Link
                            to="/case-evaluator"
                            data-testid="blog-callout-evaluator"
                            className="text-[#9FB3D4] text-sm underline underline-offset-4 decoration-[#1F3458] hover:text-[#D4AF37] hover:decoration-[#D4AF37] transition-colors"
                        >
                            Prefer to start online? Take the 60-second case
                            evaluator →
                        </Link>
                    </div>
                </aside>
            );
        default:
            return null;
    }
}

export default function BlogPost() {
    const { slug } = useParams();
    const post = getPostBySlug(slug);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [slug]);

    if (!post) return <Navigate to="/blog" replace />;

    const articleSchema = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "@id": `${SITE_URL}/blog/${post.slug}#post`,
        headline: post.title,
        description: post.excerpt,
        url: `${SITE_URL}/blog/${post.slug}`,
        datePublished: post.dateISO,
        dateModified: `${post.updated}T08:00:00-08:00`,
        inLanguage: "en-US",
        keywords: [
            post.keyword,
            "accident lawyer Sacramento",
            "personal injury attorney Sacramento",
            "personal injury lawyer Placer County",
            "Joe Helfrick",
        ].join(", "),
        articleSection: post.category,
        wordCount: post.blocks.reduce((n, b) => {
            if (b.type === "p" || b.type === "h2" || b.type === "h3")
                return n + (b.text || "").split(/\s+/).length;
            if (b.type === "ul" || b.type === "ol")
                return (
                    n +
                    b.items.reduce(
                        (m, it) =>
                            m +
                            (typeof it === "string"
                                ? it
                                : `${it.title || ""} ${it.body || ""}`
                            ).split(/\s+/).length,
                        0,
                    )
                );
            return n;
        }, 0),
        image: {
            "@type": "ImageObject",
            url: `${SITE_URL}${post.image}`,
            width: 1200,
            height: 630,
        },
        author: { "@id": `${SITE_URL}/#person` },
        publisher: { "@id": `${SITE_URL}/#attorney` },
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `${SITE_URL}/blog/${post.slug}`,
        },
        about: [
            { "@type": "Place", name: "Sacramento, California" },
            { "@type": "Place", name: "Sacramento County, California" },
            { "@type": "Place", name: "Placer County, California" },
            { "@type": "Thing", name: "Personal Injury Law" },
            { "@type": "Thing", name: "California Statute of Limitations" },
        ],
        speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: [".tldr", "h1", "h2"],
        },
    };

    // Optional HowTo schema — only when the post declares one and contains an ordered-list block
    const howToBlock = post.howTo
        ? post.blocks.find((b) => b.type === "ol")
        : null;
    const howToSchema =
        post.howTo && howToBlock
            ? {
                  "@context": "https://schema.org",
                  "@type": "HowTo",
                  name: post.howTo.name,
                  description: post.howTo.description,
                  totalTime: post.howTo.totalTime,
                  image: `${SITE_URL}${post.image}`,
                  step: howToBlock.items.map((it, i) => ({
                      "@type": "HowToStep",
                      position: i + 1,
                      name: it.title,
                      text: it.body,
                  })),
              }
            : null;

    // Attach HowTo to the main page schema as @graph so we can emit both.
    const pageSchema = howToSchema
        ? { "@context": "https://schema.org", "@graph": [articleSchema, howToSchema] }
        : articleSchema;

    return (
        <>
            <SEO
                title={post.title}
                description={post.excerpt}
                path={`/blog/${post.slug}`}
                image={`${SITE_URL}${post.image}`}
                schema={pageSchema}
                faqs={post.faqs}
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Blog", path: "/blog" },
                    { name: post.h1, path: `/blog/${post.slug}` },
                ]}
            />

            <article>
                {/* HEADER */}
                <header
                    data-testid="post-header"
                    className="border-b border-[#1F3458]"
                >
                    <div className="max-w-[900px] mx-auto px-6 lg:px-8 pt-16 pb-12 lg:pt-24">
                        <Link
                            to="/blog"
                            data-testid="post-back"
                            className="inline-flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.22em] font-bold mb-8 hover:text-[#E6C869]"
                        >
                            <ArrowLeft size={14} /> All Articles
                        </Link>
                        <div className="text-[#D4AF37] text-[11px] uppercase tracking-[0.28em] font-bold">
                            {post.category}
                        </div>
                        <h1 className="mt-5 font-serif text-4xl md:text-6xl text-[#F7F5EB] leading-[1] font-light">
                            {post.h1}
                        </h1>
                        <p className="mt-6 text-lg md:text-xl text-[#9FB3D4] leading-relaxed">
                            {post.subtitle}
                        </p>
                        <div className="mt-8 flex flex-wrap gap-5 text-xs text-[#9FB3D4]">
                            <span className="flex items-center gap-2">
                                By Joseph G. Helfrick
                            </span>
                            <span className="flex items-center gap-2">
                                <Calendar size={12} />
                                {new Date(post.date).toLocaleDateString(
                                    "en-US",
                                    {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                    },
                                )}
                            </span>
                            <span className="flex items-center gap-2">
                                <Clock size={12} />
                                {post.readTime}
                            </span>
                        </div>
                    </div>
                </header>

                {/* TL;DR */}
                <section className="border-b border-[#1F3458]">
                    <div className="max-w-[900px] mx-auto px-6 lg:px-8 py-12">
                        <div
                            className="tldr border-l-2 border-[#D4AF37] pl-6 py-2"
                            data-speakable
                        >
                            <div className="text-[#D4AF37] text-[10px] uppercase tracking-[0.28em] font-bold mb-3">
                                Quick Answer
                            </div>
                            <p className="text-[#F7F5EB]/95 text-base md:text-lg leading-relaxed">
                                {post.tldr}
                            </p>
                        </div>
                    </div>
                </section>

                {/* BODY */}
                <section>
                    <div className="max-w-[900px] mx-auto px-6 lg:px-8 py-16">
                        {post.blocks.map((b, i) => (
                            <Block key={i} block={b} />
                        ))}
                    </div>
                </section>

                {/* FAQs */}
                {post.faqs && post.faqs.length > 0 && (
                    <section
                        data-testid="post-faqs"
                        className="border-t border-[#1F3458] bg-[#0B1A36]"
                    >
                        <div className="max-w-[900px] mx-auto px-6 lg:px-8 py-20">
                            <div className="text-[#D4AF37] text-[11px] uppercase tracking-[0.28em] font-bold">
                                Frequently Asked
                            </div>
                            <h2 className="mt-3 font-serif text-3xl md:text-4xl text-[#F7F5EB]">
                                {post.keyword.charAt(0).toUpperCase() +
                                    post.keyword.slice(1)}{" "}
                                — quick answers
                            </h2>
                            <div className="mt-8 divide-y divide-[#1F3458]">
                                {post.faqs.map((f, i) => (
                                    <details
                                        key={i}
                                        data-testid={`post-faq-${i}`}
                                        className="group py-6"
                                    >
                                        <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                                            <span className="font-serif text-xl md:text-2xl text-[#F7F5EB] leading-snug">
                                                {f.q}
                                            </span>
                                            <span className="text-[#D4AF37] text-2xl transition-transform group-open:rotate-45 leading-none">
                                                +
                                            </span>
                                        </summary>
                                        <p className="mt-4 text-[#9FB3D4] leading-relaxed">
                                            {f.a}
                                        </p>
                                    </details>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* AUTHOR CARD — E-E-A-T */}
                <section
                    data-testid="post-author"
                    className="border-t border-[#1F3458]"
                >
                    <div className="max-w-[900px] mx-auto px-6 lg:px-8 py-16">
                        <div className="text-[#D4AF37] text-[11px] uppercase tracking-[0.28em] font-bold mb-6">
                            About the Author
                        </div>
                        <div className="grid md:grid-cols-[160px_1fr] gap-8 items-start">
                            <div className="border border-[#1F3458] w-40 aspect-[3/4] overflow-hidden">
                                <img
                                    src="/images/joe-helfrick-portrait.jpg"
                                    alt="Joseph G. Helfrick — Sacramento personal injury attorney"
                                    className="w-full h-full object-cover object-center"
                                />
                            </div>
                            <div>
                                <div className="font-serif text-2xl text-[#F7F5EB]">
                                    Joseph G. Helfrick
                                </div>
                                <div className="text-[#D4AF37] text-xs uppercase tracking-[0.22em] font-bold mt-1">
                                    Personal Injury Attorney · Sacramento, CA
                                </div>
                                <p className="mt-4 text-[#9FB3D4] text-sm leading-relaxed">
                                    Joe Helfrick is a Sacramento-born trial
                                    attorney representing the seriously injured
                                    throughout Sacramento County and Placer
                                    County. B.A. History, Holy Cross College of
                                    Notre Dame (2010). J.D., Lincoln Law School
                                    (2015) — Faculty Achievement Award, Legal
                                    Analysis. Admitted to the California State
                                    Bar (2017) and the U.S. District Court,
                                    Eastern District of California (2020).
                                </p>
                                <div className="mt-5 flex flex-wrap gap-4 text-xs">
                                    <Link
                                        to="/about"
                                        className="text-[#D4AF37] font-semibold border-b border-[#D4AF37]/40 hover:border-[#D4AF37] pb-0.5 transition-colors"
                                    >
                                        Full attorney bio →
                                    </Link>
                                    <a
                                        href="https://ltglaw.net/team/joseph-helfrick/"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-[#D4AF37] font-semibold border-b border-[#D4AF37]/40 hover:border-[#D4AF37] pb-0.5 transition-colors"
                                    >
                                        LTG Law profile →
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* RELATED POSTS */}
                {POSTS.filter((p) => p.slug !== post.slug).length > 0 && (
                    <section
                        data-testid="post-related"
                        className="border-t border-[#1F3458] bg-[#0B1A36]"
                    >
                        <div className="max-w-[900px] mx-auto px-6 lg:px-8 py-16">
                            <div className="text-[#D4AF37] text-[11px] uppercase tracking-[0.28em] font-bold mb-6">
                                Keep Reading
                            </div>
                            <div className="grid md:grid-cols-2 gap-5">
                                {POSTS.filter((p) => p.slug !== post.slug)
                                    .slice(0, 2)
                                    .map((p) => (
                                        <Link
                                            key={p.slug}
                                            to={`/blog/${p.slug}`}
                                            data-testid={`related-${p.slug}`}
                                            className="group block bg-[#11203D] border border-[#1F3458] hover:border-[#D4AF37] transition-colors p-6"
                                        >
                                            <div className="text-[#9FB3D4] text-[10px] uppercase tracking-[0.22em]">
                                                {p.category}
                                            </div>
                                            <h3 className="mt-3 font-serif text-xl text-[#F7F5EB] leading-tight">
                                                {p.h1}
                                            </h3>
                                            <div className="mt-4 inline-flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.22em] font-bold">
                                                Read <ArrowRight size={12} />
                                            </div>
                                        </Link>
                                    ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* CTA */}
                <section className="border-t border-[#1F3458]">
                    <div className="max-w-[900px] mx-auto px-6 lg:px-8 py-20 text-center">
                        <h2 className="font-serif text-4xl md:text-5xl text-[#F7F5EB] leading-tight">
                            Talk to Joe.{" "}
                            <span className="italic text-[#D4AF37]">
                                The first call is free.
                            </span>
                        </h2>
                        <p className="mt-5 text-[#9FB3D4] max-w-xl mx-auto">
                            Free, confidential, no obligation. You pay nothing
                            unless we recover money for you.
                        </p>
                        <div className="mt-8 flex flex-wrap justify-center gap-4">
                            <Link
                                to="/contact"
                                data-testid="post-cta-contact"
                                className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#07142B] font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-[#B8962A] transition-colors"
                            >
                                Free Case Review <ArrowRight size={16} />
                            </Link>
                            <a
                                href={`tel:${ATTORNEY.phone.replace(/[^0-9]/g, "")}`}
                                data-testid="post-cta-phone"
                                className="inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-[#D4AF37] hover:text-[#07142B] transition-colors"
                            >
                                Call {ATTORNEY.phone}
                            </a>
                        </div>
                        <div className="mt-6">
                            <Link
                                to="/case-evaluator"
                                data-testid="post-cta-evaluator"
                                className="text-[#9FB3D4] text-sm underline underline-offset-4 decoration-[#1F3458] hover:text-[#D4AF37] hover:decoration-[#D4AF37] transition-colors"
                            >
                                Not ready to call? Take the 60-second case
                                evaluator →
                            </Link>
                        </div>
                    </div>
                </section>
            </article>
        </>
    );
}
